import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TagEditComponent from 'webapp/components/tag/edit';

import type Board from 'webapp/models-custom/board';
import type Tag from 'webapp/models-custom/tag';

export default class TagPopupTagsModalComponent extends PopupInnerModalComponent<PopupData.Tag.Modal> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;

	@tracked model: Tag[] = [];
	@tracked removedTags: Tag[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const model = this.mmTagsService.createModelsFromRecordsArray(
			this.mmTagsService.savedRecords,
		);
		this.model = model;

		this.args.model.on(PopupEvent.CLOSED, this, () => {
			this.model.forEach((tag: Tag) => {
				tag.rollback();
			});
		});
	}

	focusLastRow(): void {
		const tagListElement = this.args.model.component?.popupNode?.getElementsByTagName('ul')[0];

		if (!tagListElement) {
			return;
		}

		const listItems = tagListElement.getElementsByTagName('li'),
			listItemsLength = listItems.length,
			lastRow = listItemsLength ? listItems.item(listItemsLength - 1) : null;

		if (!lastRow) {
			return;
		}

		tagListElement.scrollTo(0, listItems[0]?.scrollHeight || 0);

		const lastInput = lastRow.querySelector<HTMLInputElement>('.name input');

		if (lastInput) {
			lastInput.focus();
			lastInput.select();
		}
	}

	validate(): boolean {
		const invalidTags = this.model.filter((tag) => {
			return !tag.validate();
		});
		return invalidTags.length === 0;
	}

	@action
	addNew(): void {
		const newTag = this.mmTagsService.createNewModel();
		this.model = [...this.model, newTag];
		schedule('afterRender', this, this.reposition);
		schedule('afterRender', this, this.focusLastRow);
	}

	@action
	deleteTag(tag: Tag): void {
		this.model = this.model.filter((t) => t !== tag);
		if (tag.id) {
			this.removedTags.push(tag);
		}
		schedule('afterRender', this, this.reposition);
	}

	@action
	save(): void {
		if (this.submitInProgress) {
			return;
		}

		const deleteRequests = this.removedTags.map((tag) => {
			// remove references to this tag from models
			this.mmBoardsService.normalBoardModels.forEach((board: Board) => {
				if (tag.numericId) {
					board.model.tags = board.model.tags.filter((t) => t !== tag.numericId);
				}
			});
			return tag.delete();
		});

		if (!this.validateTagsNames() || !this.validate()) {
			const node = this.args.model.component?.popupNode;
			if (node) {
				node.querySelector('.errorMessage.shown')?.scrollIntoView({ behavior: 'smooth' });
			}

			return;
		}

		const createdTags = this.model.filter((item) => !item.id),
			createRequests = createdTags.map((tag) => {
				this.analyticsService.event(TrackingEvent.TAGS_CREATED_NEW);
				return tag.save();
			});

		const editRequests = this.model
			.filter((item) => item.id)
			.map((tag) => {
				return tag.save();
			});

		this.submitInProgress = true;
		const infolayer = this.infolayerService.loading('Saving tags changes...');

		void Promise.allSettled([...createRequests, ...editRequests, ...deleteRequests]).then(
			(statusArray) => {
				const rejectedRequests = statusArray.filter((item) => item.status === 'rejected');

				this.submitInProgress = false;

				if (rejectedRequests.length) {
					infolayer.error('Some changes might not be saved.');
				} else {
					infolayer.ok('Tags changes saved!');
					super.close();

					if (createdTags.length > 0 && this.popupData.onCreatedTagsSaved) {
						this.popupData.onCreatedTagsSaved(createdTags);
					}
				}
			},
		);
	}

	addNewTagOnInit(): void {
		later(() => {
			this.addNew();
		}, 200); // bcos we focus first input type after loading modal
	}

	@action
	setupView(): void {
		if (this.popupData.triggerAddNew) {
			schedule('afterRender', this, this.addNewTagOnInit);
		}
	}

	@action
	validateTagsNames(): boolean {
		let success = true;
		const nameGroups = new Map<string, Tag[]>();
		this.model.forEach((tag) => {
			if (!nameGroups.has(tag.name)) {
				nameGroups.set(tag.name, []);
			}
			nameGroups.get(tag.name)!.push(tag);
		});

		Array.from(nameGroups.values()).forEach((tags) => {
			if (tags.length > 1) {
				tags.forEach((tag) => {
					tag.setValidationErrors({
						name: [{ message: 'Tag with such name already exists!' }],
					});
				});
				success = false;
			} else {
				tags[0]!.setValidationErrors({});
			}
		});

		return success;
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="TagFill" @size="lg" />
			Manage tags
		</div>
		<div class="content">
			<ul {{didInsert this.setupView}}>
				{{#each this.model as |tag|}}
					<TagEditComponent
						@model={{tag}}
						@isDisabled={{this.submitInProgress}}
						@onDelete={{this.deleteTag}}
						@validateTagsNames={{this.validateTagsNames}}
					/>
				{{else}}
					<li>No tags created yet.</li>
				{{/each}}
			</ul>
			<a role="button" class="add" {{on "click" this.addNew}}>New tag</a>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		</div>
	</template>
}
